import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { ArrowLeft, X } from "lucide-react";
import type { ReactNode } from "react";
import type { MergedBooking } from "@/utils/mergeSlots";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

type WizardStep = "type" | "details" | "payment" | "confirmation";

interface WizardHeaderProps {
  // Generic step (Alt B refactor: was literal WizardStep, now string|number
  // so non-booking consumers like ActivityWizard can pass arbitrary step ids).
  step: WizardStep | string | number;
  onBack: () => void;
  onClose: () => void;
  // Booking-specific props — now all optional (Alt B refactor 2026-05-27)
  // so the component can serve non-booking wizards (e.g. ActivityWizard).
  // BookingWizard continues to pass all three; activity-wizard passes none.
  mergedBookings?: MergedBooking[];
  bookingType?: "private" | "open_match" | null;
  selectedDate?: Date;
  // Generic-overrides (Alt B refactor) — when set, override the booking-default
  // rendering of step-number, summary text, and the booking-type badge.
  customStepNumber?: number;       // override stepNumber-lookup for non-booking flows
  customStepLabel?: string;        // override "stepOf"-text suffix (e.g. "(steg X av Y)")
  customSummary?: ReactNode;       // replaces booking court/time summary if passed
  hideBookingTypeBadge?: boolean;  // skip booking-type badge unconditionally
  hideBackOnSteps?: Array<string | number>;  // override default "hide back on first/last step"
  i18nNamespace?: string;          // default "booking"; activity passes "admin"
  // Structural-chrome string override (Alt B refactor): the back-button label
  // lives in the "booking" namespace as t("backLabel"). Non-booking consumers
  // (activity-wizard uses "admin" namespace) must pass their own translated
  // string so the raw key isn't rendered.
  backLabel?: string;
  // Livfull (väg A, additive): total step count for the gradient progress bar. When set,
  // a `totalSteps`-segment bar renders (segments 1..num filled). ActivityWizard passes 6,
  // BookingWizard passes 3. Purely presentational — does not affect any handler/derivation.
  totalSteps?: number;
}

const stepNumber: Record<WizardStep, number> = {
  type: 1,
  details: 2,
  payment: 3,
  confirmation: 3,
};

const _stepLabels: Record<WizardStep, string> = {
  type: "",
  details: "",
  payment: "confirmAndPay",
  confirmation: "",
};

export default function WizardHeader({
  step,
  onBack,
  onClose,
  mergedBookings,
  bookingType,
  selectedDate,
  customStepNumber,
  customStepLabel,
  customSummary,
  hideBookingTypeBadge,
  hideBackOnSteps,
  i18nNamespace,
  backLabel,
  totalSteps,
}: WizardHeaderProps) {
  const { t } = useTranslation(i18nNamespace ?? "booking");
  // Step-number resolution: customStepNumber wins, else lookup booking-step-literal.
  const num =
    customStepNumber ??
    (typeof step === "string" && step in stepNumber ? stepNumber[step as WizardStep] : 0);
  // Default booking-behavior: hide back on "type" + "confirmation". Allow override
  // for non-booking flows via hideBackOnSteps.
  const showBack = hideBackOnSteps
    ? !hideBackOnSteps.includes(step)
    : step !== "type" && step !== "confirmation";
  const isMulti = (mergedBookings?.length ?? 0) > 1;
  const mergedSafe = mergedBookings ?? [];

  const dateStr = selectedDate ? format(selectedDate, "EEE d MMM", { locale: sv }) : "";

  // Build court/time summary (booking-specific — only computed if booking-props passed)
  let summary: string;
  if (isMulti) {
    summary = mergedSafe
      .map(
        (b) =>
          `${b.courtName} ${format(new Date(b.startTime), "HH:mm")}–${format(new Date(b.endTime), "HH:mm")}`
      )
      .join(" · ");
  } else if (mergedSafe.length === 1) {
    const b = mergedSafe[0];
    summary = `${b.courtName} · ${dateStr} · ${format(new Date(b.startTime), "HH:mm")}–${format(new Date(b.endTime), "HH:mm")}`;
  } else {
    summary = dateStr;
  }

  const typeIcon =
    bookingType === "private" ? "🔒" : bookingType === "open_match" ? "🌍" : null;
  const typeLabel =
    bookingType === "private"
      ? t("privateBooking")
      : bookingType === "open_match"
        ? t("openMatch")
        : null;
  const showBookingTypeBadge =
    !hideBookingTypeBadge && step !== "type" && step !== "confirmation" && typeLabel;
  // Step-counter line is suppressed on booking "confirmation" by default. For
  // non-booking step values, show the counter unless customStepLabel is empty-string.
  const showStepCounter = step !== "confirmation" && customStepLabel !== "";

  return (
    <div className="border-b px-4 py-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        {/* Livfull: back as a rounded icon button (was icon+text). Handler unchanged; the former
            visible label becomes the aria-label (reuses the existing string, no new i18n key). */}
        {showBack ? (
          <button
            onClick={onBack}
            aria-label={backLabel ?? t("backLabel")}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border text-muted-foreground transition-colors hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        ) : (
          <span className="h-9 w-9 shrink-0" aria-hidden />
        )}
        {showStepCounter && (
          <span className="flex-1 text-center text-[11px] font-semibold uppercase tracking-wide text-primary">
            {customStepLabel ?? t("stepOf", { num })}
          </span>
        )}
        <button
          onClick={onClose}
          className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border text-muted-foreground transition-colors hover:text-foreground"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Livfull gradient progress bar — segments 1..num filled. Additive: only when totalSteps set. */}
      {totalSteps != null && num > 0 && (
        <div className="flex gap-1.5" aria-hidden>
          {Array.from({ length: totalSteps }, (_, i) => (
            <span
              key={i}
              className={cn("h-1.5 flex-1 rounded-full", i < num ? "livfull-progress-fill" : "bg-muted")}
            />
          ))}
        </div>
      )}

      {/* Booking type badge on step 2+ (suppressed when hideBookingTypeBadge or no bookingType) */}
      {showBookingTypeBadge && (
        <p className="text-sm font-medium">
          {typeIcon} {typeLabel}
        </p>
      )}

      {/* Summary line — customSummary wins; falls back to booking court/time */}
      {customSummary ? (
        <p className="text-xs text-muted-foreground leading-snug">{customSummary}</p>
      ) : (
      <p className="text-xs text-muted-foreground leading-snug">
        {isMulti && (
          <span className="font-medium text-foreground">
            {mergedSafe.length} {t("times")} · {dateStr}
            <br />
          </span>
        )}
        {isMulti
          ? mergedSafe
              .map(
                (b) =>
                  `${b.courtName} ${format(new Date(b.startTime), "HH:mm")}–${format(new Date(b.endTime), "HH:mm")}`
              )
              .join(" · ")
          : summary}
      </p>
      )}
    </div>
  );
}
